import {
  createCounterDelivery,
  CreateCounterDeliveryProps,
  getCounterDeliveries,
  GetCounterDeliveriesProps,
  getCounterDelivery,
  updateCounterDelivery,
  UpdateCounterDeliveryProps,
} from "@/services/counter-delivery.service";
import { Dispatch, RefObject, SetStateAction } from "react";
import {
  CounterDeliveryDetail,
  CounterDeliveryListResponse,
} from "./counter-delivery.schema";
import { NavigateFunction } from "react-router-dom";
import { AxiosError } from "axios";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { getCuentasByBusqueda } from "@/services/accounts.service";
import { Option } from "@/components/select/AsyncAutocompleteSelect";
import { CDFRProps, getCounterDeliveriesFilter } from "./filter-obj";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { AppRoutes } from "@/constants/routes";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { EstadosContraEntrega } from "@/constants/enums/status/entity-status-ids";

export interface FetchCounterDeliveriesHandlerProps {
  setLoadingCounterDeliveries: Dispatch<SetStateAction<boolean>>;
  filters?: GetCounterDeliveriesProps;
  showLoad?: boolean;
  setCounterDeliveries: Dispatch<
    SetStateAction<CounterDeliveryListResponse | null | undefined>
  >;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchCounterDeliveriesHandler = async ({
  setLoadingCounterDeliveries,
  filters,
  showLoad,
  setCounterDeliveries,
  navigate,
  fetchPermissions,
}: FetchCounterDeliveriesHandlerProps) => {
  try {
    if (showLoad) setLoadingCounterDeliveries(true);

    const response = await getCounterDeliveries({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setCounterDeliveries(response);

    setLoadingCounterDeliveries(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo obtener las solicitudes de granos a entregar.",
      });
    }
    throw error;
  } finally {
    setLoadingCounterDeliveries(false);
  }
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("delivery_date", undefined);
  setValue("account_owner", "");
  setValue("delivery_amount", "");
  setValue("estados", []);
  setValue("ton", "");
  setValue("grain", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<
    SetStateAction<Partial<GetCounterDeliveriesProps> | undefined>
  >;
  fetchCounterDeliveries: (filters?: any) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchCounterDeliveries,
  setOpenFilters,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetCounterDeliveriesProps> =
    getCounterDeliveriesFilter(event as CDFRProps);

  setCurrentFilters(filterObj);

  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  await fetchCounterDeliveries(filterObj);

  setOpenFilters(false);
};

export interface GetAccountOptionsHandlerProps {
  inputValue: string;
}

export const getOptionsHandler = async ({
  inputValue,
}: GetAccountOptionsHandlerProps) => {
  const filtered = await getCuentasByBusqueda({
    busqueda: inputValue,
  });

  return (
    filtered?.map((p) => ({
      label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,
      value: p.nroCuenta,
      tipoCuenta: p.tipoCuenta,
      idTipoCuenta: p.idTipoCuenta,
    })) ?? []
  );
};

export const loadOptionsHandler = ({
  timeoutRef,
  inputValue,
  getOptions,
  setLoadingPersons,
}: LoadOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef?.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (inputValue.trim().length < 3) {
        resolve([]);
        return;
      }

      setLoadingPersons(true);
      const options = getOptions(inputValue);

      setLoadingPersons(false);
      resolve(options);
    }, 1000);
  });
};

export interface LoadOptionsHandlerProps {
  timeoutRef: RefObject<NodeJS.Timeout | null>;
  inputValue: string;
  setLoadingPersons: Dispatch<SetStateAction<boolean>>;
  getOptions: (inputValue: string) => Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  >;
}

export interface CreateCounterDeliveryHandlerProps {
  navigate: NavigateFunction;
  setLoadingCreateCounterDelivery: Dispatch<SetStateAction<boolean>>;
  watchedValues: any;
  fetchPermissions: () => Promise<void>;
}

export const createCounterDeliveryHandler = async ({
  navigate,
  setLoadingCreateCounterDelivery,
  fetchPermissions,
  watchedValues,
}: CreateCounterDeliveryHandlerProps) => {
  setLoadingCreateCounterDelivery(true);

  try {
    const parsedTon =
      typeof watchedValues?.ton === "string"
        ? Number.parseFloat(watchedValues.ton.replace(",", "."))
        : watchedValues?.ton;

    const objToSend: CreateCounterDeliveryProps = {
      nroCuentaOrigen: watchedValues?.account?.value,
      grano: watchedValues?.grain,
      toneladas: parsedTon,
      cosecha: watchedValues?.harvest?.[0],
      monto: formatCurrencyInputToNumber(watchedValues?.price ?? 0),
      esCanje: watchedValues?.isExchange,
      mensaje: watchedValues?.order_detail,
    };

    await createCounterDelivery(objToSend);

    toaster.create({
      title: "Venta de granos a entregar creada con éxito",
      type: "success",
    });
    setLoadingCreateCounterDelivery(false);
    navigate(AppRoutes.GRAIN_DELIVERY_PAYMENTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.GRAIN_DELIVERY_PAYMENTS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj ?? "No se ha podido crear la contra entrega.",
        duration: 6000,
      });
    }
    throw error;
  } finally {
    setLoadingCreateCounterDelivery(false);
  }
};

export interface HandleSendNegotiationHandlerProps {
  setLoadingSendNegotiation: Dispatch<SetStateAction<boolean>>;
  counterDelivery?: CounterDeliveryDetail | null;
  fetchCounterDelivery: (showLoad?: boolean) => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const sendCounterDeliveryNegotiationHandler = async ({
  setLoadingSendNegotiation,
  counterDelivery,
  fetchCounterDelivery,
  navigate,
  fetchPermissions,
}: HandleSendNegotiationHandlerProps) => {
  setLoadingSendNegotiation(true);
  try {
    const objToSend: UpdateCounterDeliveryProps = {
      idSolicitud: counterDelivery?.id ?? 0,
      estado: EstadosContraEntrega.Negociacion
    };

    await updateCounterDelivery(objToSend);

    toaster.create({
      type: "success",
      title: "Solicitud enviada a negociación.",
    });

    await fetchCounterDelivery(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo enviar la solicitud a negociación.",
      });
      throw error;
    }
  } finally {
    setLoadingSendNegotiation(false);
  }
};

export interface HandleSendCommunicationHandlerProps {
  setLoadingSendCommunication: Dispatch<SetStateAction<boolean>>;
  event?: FieldValues;
  counterDelivery?: CounterDeliveryDetail | null;
  fetchCounterDelivery: (showLoad?: boolean) => Promise<void>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setOpenCommunicationModal: Dispatch<SetStateAction<boolean>>;
}

export const sendCounterDeliveryCommunicationHandler = async ({
  setLoadingSendCommunication,
  event,
  counterDelivery,
  fetchCounterDelivery,
  navigate,
  fetchPermissions,
  setOpenCommunicationModal,
}: HandleSendCommunicationHandlerProps) => {
  setLoadingSendCommunication(true);
  try {
    const objToSend: UpdateCounterDeliveryProps = {
      idSolicitud: counterDelivery?.id ?? 0,
      estado: EstadosContraEntrega.EnComunicacion,
      mensaje: event?.request_detail,
    };

    await updateCounterDelivery(objToSend);

    toaster.create({
      type: "success",
      title: "Comunicado realizado exitosamente",
    });

    await fetchCounterDelivery(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "Hubo un error al enviar el comunicado.",
      });
      throw error;
    }
  } finally {
    setLoadingSendCommunication(false);
  }

  setOpenCommunicationModal(false);
};

export interface HandleRejectCounterDeliveryHandlerProps {
  event?: FieldValues;
  setLoadingRejectCounterDelivery: Dispatch<SetStateAction<boolean>>;
  counterDelivery?: CounterDeliveryDetail | null;
  navigate: NavigateFunction;
  setOpenRejectModal: Dispatch<SetStateAction<boolean>>;
  fetchPermissions: () => Promise<void>;
}
export const handleRejectCounterDeliveryHandler = async ({
  setLoadingRejectCounterDelivery,
  event,
  counterDelivery,
  navigate,
  setOpenRejectModal,
  fetchPermissions,
}: HandleRejectCounterDeliveryHandlerProps) => {
  setLoadingRejectCounterDelivery(true);
  try {
    const objToSend: UpdateCounterDeliveryProps = {
      idSolicitud: Number(counterDelivery?.id),
      mensaje: event?.request_detail,
      estado: EstadosContraEntrega.Rechazada,
    };

    await updateCounterDelivery(objToSend);

    toaster.create({
      type: "success",
      title: `Venta de granos a entregar rechazada exitosamente.`,
    });

    setLoadingRejectCounterDelivery(false);

    navigate(AppRoutes.GRAIN_DELIVERY_PAYMENTS);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string; errors?: string }>;

    const errorMsj = errorObj?.response?.data?.errors;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMsj ?? "No se pudo rechazar la venta de granos a entregar.",
      });
      throw error;
    }
  } finally {
    setLoadingRejectCounterDelivery(false);
  }

  setOpenRejectModal(false);
};

export interface HandleExecuteCounterDeliveryProps {
  setLoadingExecuteCounterDelivery: Dispatch<SetStateAction<boolean>>;
  counterDelivery?: CounterDeliveryDetail | null;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  watchedValues?: FieldValues
}

export const handleExecuteCounterDeliveryHandler = async ({
  setLoadingExecuteCounterDelivery,
  navigate,
  counterDelivery,
  fetchPermissions,
  watchedValues
}: HandleExecuteCounterDeliveryProps) => {
  setLoadingExecuteCounterDelivery(true);
  try {
    const objToSend: UpdateCounterDeliveryProps = {
      idSolicitud: counterDelivery?.id || 0,
      estado: EstadosContraEntrega.Confirmada,
      precioFinal: formatCurrencyInputToNumber(watchedValues?.final_price)
    };

    await updateCounterDelivery(objToSend);

    toaster.create({
      type: "success",
      title: `Venta de granos a entregar finalizada exitosamente.`,
    });

    setLoadingExecuteCounterDelivery(false);

    navigate(AppRoutes.GRAIN_DELIVERY_PAYMENTS);
  } catch (error) {
   
    const errorMsj = getAxiosErrorMessage(
      error,
      "No se pudo finalizar la venta de granos a entregar.",
    );

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorMsj 
      });
    }
    throw error;
  } finally {
    setLoadingExecuteCounterDelivery(false);
  }
};

export interface UpdateCounterDeliveryAmountHandlerProps {
  errors: any;
  setLoadingEditAmount: Dispatch<SetStateAction<boolean>>;
  counterDelivery: any;
  fetchCounterDelivery: (showLoad: boolean) => Promise<void>;
  setIsEditingAmount: Dispatch<SetStateAction<boolean>>;
  watchedValues: any;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const updateCounterDeliveryAmountHandler = async ({
  errors,
  setLoadingEditAmount,
  counterDelivery,
  fetchCounterDelivery,
  setIsEditingAmount,
  navigate,
  fetchPermissions,
  watchedValues,
}: UpdateCounterDeliveryAmountHandlerProps) => {
  if (errors?.counter_delivery_amount) return;
  try {
    setLoadingEditAmount(true);

    await updateCounterDelivery({
      idSolicitud: counterDelivery?.id ?? 0,
      monto: formatCurrencyInputToNumber(
        watchedValues?.counter_delivery_amount,
      ),
    });

    await fetchCounterDelivery(false);

    setLoadingEditAmount(false);
    setIsEditingAmount((prev) => !prev);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (handled) setIsEditingAmount(false);
    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo editar el monto solicitado.",
      });
    }

    throw error;
  } finally {
    setLoadingEditAmount(false);
  }
};

export interface FetchCounterDeliveryDetailProps {
  id?: string;
  showLoad: boolean;
  setLoadingCounterDelivery: Dispatch<SetStateAction<boolean>>;
  setCounterDelivery: Dispatch<
    SetStateAction<CounterDeliveryDetail | null | undefined>
  >;
  navigate: NavigateFunction
  fetchPermissions: () => Promise<void>
}

export const fetchCounterDeliveryHandler = async ({
  id,
  setLoadingCounterDelivery,
  showLoad,
  setCounterDelivery,
  fetchPermissions,
  navigate
}: FetchCounterDeliveryDetailProps) => {
  try {
    if (showLoad) setLoadingCounterDelivery(true);
    const response = await getCounterDelivery({
      id: Number(id),
    });

    setCounterDelivery(response);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;

    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ??
          "No se pudo obtener la solicitud.",
      });
    }
    throw error;
  } finally {
    setLoadingCounterDelivery(false);
  }
};
